<template>
  <InfuseModal :title="t('title')" @close="$emit('close')">
    <div class="mb-4 flex flex-col gap-4">
      <InfuseField :label="t('field.floorLevel.label')" :optional="true" size="small">
        <InfuseInput
          v-if="updatedEvseRoamingSettings"
          v-model="updatedEvseRoamingSettings.floorLevel"
          size="xs"
          :maxlength="4"
          :placeholder="t('field.floorLevel.placeholder')"
        />
      </InfuseField>

      <InfuseField :label="t('field.physicalReference.label')" :optional="true" size="small">
        <InfuseInput
          v-if="updatedEvseRoamingSettings"
          v-model="updatedEvseRoamingSettings.physicalReference"
          size="xs"
          :maxlength="16"
          :placeholder="t('field.physicalReference.placeholder')"
        />
      </InfuseField>

      <InfuseField :label="t('field.parkingRestrictions')" :optional="true" size="small">
        <ParkingRestrictionSelector
          v-if="updatedEvseRoamingSettings"
          :evse-id="evse.ocppEvseId"
          :read-only="false"
          :selected-parking-restrictions="updatedEvseRoamingSettings.parkingRestrictions"
          class="mt-1"
          @select="(parkingRestriction: ParkingRestriction) => selectParkingRestriction(parkingRestriction)"
        />
      </InfuseField>

      <InfuseField :label="t('field.capabilities')" :optional="true" size="small">
        <CapabilitySelector
          v-if="updatedEvseRoamingSettings"
          :evse-id="evse.ocppEvseId"
          :read-only="false"
          :selected-capabilities="updatedEvseRoamingSettings.capabilities"
          class="mt-1"
          @select="(capability: Capability) => selectCapability(capability)"
        />
      </InfuseField>
    </div>

    <template #actions>
      <InfuseButton data-testid="modal-confirm-button" type="submit" @click="save">{{ t('save') }}</InfuseButton>
    </template>
  </InfuseModal>
</template>

<script lang="ts" setup>
import { Capability, Evse, ParkingRestriction } from '@infuse/cpms/charging-station/store';
import { CapabilitySelector } from '@infuse/cpms/config/ui';

import { InfuseButton, InfuseField, InfuseInput, InfuseModal } from '@infuse/ui/components';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { EvseRoamingSettings } from './types/RoamingSettings';
import ParkingRestrictionSelector from './ParkingRestrictionSelector.vue';

interface Props {
  evse: Evse;
}
const props = defineProps<Props>();

interface Emits {
  (event: 'close'): void;
  (event: 'save', updatedEvseRoamingSettings: EvseRoamingSettings): void;
}
const emit = defineEmits<Emits>();

const { t } = useI18n();

const updatedEvseRoamingSettings = ref<EvseRoamingSettings>();

onMounted(() => {
  // Initialize with the current settings.
  updatedEvseRoamingSettings.value = {
    floorLevel: props.evse.floorLevel || '',
    physicalReference: props.evse.physicalReference || '',
    parkingRestrictions: props.evse.parkingRestrictions?.slice() || [],
    capabilities: props.evse.capabilities?.slice() || [],
    evseId: props.evse.ocppEvseId
  };
});

const selectCapability = (capability: Capability) => {
  if (!updatedEvseRoamingSettings.value) {
    return;
  }

  const index = updatedEvseRoamingSettings.value.capabilities.findIndex((c) => c === capability);
  if (index !== -1) {
    updatedEvseRoamingSettings.value.capabilities.splice(index, 1);
  } else {
    updatedEvseRoamingSettings.value.capabilities.push(capability);
  }
};

const selectParkingRestriction = (parkingRestriction: ParkingRestriction) => {
  if (!updatedEvseRoamingSettings.value) {
    return;
  }

  const index = updatedEvseRoamingSettings.value.parkingRestrictions.findIndex((pr) => pr === parkingRestriction);
  if (index !== -1) {
    updatedEvseRoamingSettings.value.parkingRestrictions.splice(index, 1);
  } else {
    updatedEvseRoamingSettings.value.parkingRestrictions.push(parkingRestriction);
  }
};

const save = () => {
  if (!updatedEvseRoamingSettings.value) {
    return;
  }

  // Do not allow empty strings as value.
  if (updatedEvseRoamingSettings.value.floorLevel === '') {
    updatedEvseRoamingSettings.value.floorLevel = undefined;
  }
  if (updatedEvseRoamingSettings.value.physicalReference === '') {
    updatedEvseRoamingSettings.value.physicalReference = undefined;
  }

  emit('save', updatedEvseRoamingSettings.value);
};
</script>

<i18n>
{
  "en": {
    "field": {
      "capabilities": "Capabilities",
      "floorLevel": {
        "label": "Floor level",
        "placeholder": "Floor level in the locally displayed numbering scheme"
      },
      "parkingRestrictions": "Parking restrictions",
      "physicalReference": {
        "label": "Physical reference",
        "placeholder": "Visible identifier on the EVSE"
      }
    },
    "title": "Edit EVSE's roaming settings",
    "save": "Save"
  },
  "nl": {
    "field": {
      "capabilities": "Functies",
      "floorLevel": {
        "label": "Verdieping",
        "placeholder": "Verdieping zoals genummerd op locatie"
      },
      "parkingRestrictions": "Parkeerbeperkingen",
      "physicalReference": {
        "label": "Fysieke referentie",
        "placeholder": "Zichtbare identificatie op de EVSE"
      }
    },
    "title": "EVSE's roaming-instellingen aanpassen",
    "save": "Opslaan"
  }
}
</i18n>
